import React, { useState } from "react";
import { motion } from "framer-motion";
import { Moon, Sun, Bell, PlusCircle, Settings, Shield, User, FileText } from "lucide-react";
import Sidebar from "../Side/Sidebar";
import Topbar from "../Side/Topbar";
import AdminSidebar from "../Side/AdminSidebar";
import { useAuth } from "../Auth/AuthContext";
import { useNavigate } from "react-router-dom";

const AdminSettings = ({ admin, darkMode, toggleDarkMode }) => {

    const navigate = useNavigate();
    const [notifications, setNotifications] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
    };
    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://localhost:44333/api/';

    const handleSubmit = async () => {
        if (!selectedFile) {
            alert("Please select a file.");
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);  // FIX: Use "file" to match .NET API
        formData.append("userId", "admin");

        try {
            const response = await fetch(`${baseUrl}Agreement/Upload`, {  // FIX: Removed extra "}"
                method: "POST",
                body: formData,
            });

            if (response.ok) {
                alert("Agreement uploaded successfully!");
                setIsModalOpen(false);
                setSelectedFile(null);
            } else {
                const errorText = await response.text();
                alert(`Error uploading file: ${errorText}`);
            }
        } catch (error) {
            console.error("Upload error:", error);
            alert("An error occurred while uploading.");
        }
    };

    const settings = [
        {
            title: "Notifications",
            description: "Manage your notification preferences.",
            icon: Bell,
            action: () => setNotifications(!notifications),
            isToggle: true,
            active: notifications
        },
        {
            title: "Manage Users",
            description: "Add, remove, or update user information.",
            icon: User,
            action: () => navigate("/publishers"),
            isToggle: false
        },
        {
            title: "Agreement Settings",
            description: "Update agreement.",
            icon: FileText,
            action: () => setIsModalOpen(true),
            isToggle: false
        },
        {
            title: "Ad Units",
            description: "Configure ad units and placements.",
            icon: PlusCircle,
            action: () => navigate("/adunits"),
            isToggle: false
        },
        {
            title: "Ads.txt Configuration",
            description: "Manage your Ads.txt file for monetization.",
            icon: PlusCircle,
            action: () => navigate("/AdsTxtConfiguration"),
            isToggle: false
        },
        {
            title: "Ad Code Setup",
            description: "Customize your ad code configurations.",
            icon: PlusCircle,
            action: () => navigate("/AdsCodeConfiguration"),
            isToggle: false
        }
    ];

    return (
        <div className="min-h-screen flex flex-col md:flex-row">


            <AdminSidebar />
            <div className="flex-1 bg-gray-100">
                <div className="flex-1 flex flex-col">
                    <Topbar />
                    <main className="pt-[100px] px-4">
                        <div className="mt-6">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className={`p-6 max-w-4xl mx-auto rounded-lg shadow-lg ${darkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"}`}
                            >
                                <h2 className="text-2xl font-bold mb-4">Admin Settings</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {settings.map(({ title, description, icon: Icon, action, isToggle, active }, index) => (
                                        <motion.div
                                            key={index}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className={`p-4 rounded-lg shadow-md cursor-pointer flex items-center gap-4 ${darkMode ? "bg-gray-800 text-white" : "bg-gray-100"}`}
                                            onClick={action}
                                        >
                                            <Icon size={24} className={isToggle && active ? "text-green-500" : "text-gray-600"} />
                                            <div>
                                                <h3 className="font-semibold">{title}</h3>
                                                <p className="text-sm text-gray-500">{description}</p>
                                            </div>
                                            {isToggle && (
                                                <motion.button whileTap={{ scale: 0.9 }} className={`ml-auto p-2 rounded-full ${active ? "bg-green-500 text-white" : "bg-gray-300"}`}>
                                                    {active ? "ON" : "OFF"}
                                                </motion.button>
                                            )}
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </main>
                </div>
            </div>
            {/* Modal for Agreement Upload */}
            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-96">
                        <h2 className="text-xl font-semibold mb-4">Upload Agreement</h2>
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                            className="border p-2 w-full mb-4"
                        />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setIsModalOpen(false)} className="bg-gray-400 text-white px-4 py-2 rounded-md">Cancel</button>
                            <button onClick={handleSubmit} className="bg-blue-600 text-white px-4 py-2 rounded-md">Upload</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSettings;
